import axios from 'axios';
import { AppConfig, JWT } from '../config';
import { AdminUserConstant } from './constants/AdminUserConstant';
import { AlertAction } from './AlertAction';
import { Translate as t } from '../config/Translate';
// FEAT-025 — 응답 user 객체의 dealerType legacy fallback 정규화.
import { normalizeUserDealerType } from '../lib/userNormalize';
// PR #185 R2 — localStorage 'user' null 인 상태에서 .adminid 접근 시 TypeError 방어.
import { readSessionAdminIdOrAbort, readSessionUser } from '../lib/sessionUser';
import { AuthAction } from './AuthAction';

const GetUserAll = () => async (dispatch) => {
	dispatch({ type: AdminUserConstant.LOADING });
	try {
		const ret = await axios.get(AppConfig.API_SERVER_PATH + '/user', JWT());
		if (ret.data.status == 'success') {
			// FEAT-025 — 목록 응답의 모든 user 에 dealerType legacy fallback 적용.
			const normalizedUsers = Array.isArray(ret.data.data)
				? ret.data.data.map(normalizeUserDealerType)
				: ret.data.data;
			dispatch({ type: AdminUserConstant.GET_USERLIST, users: normalizedUsers });
		} else {
			dispatch(AlertAction.Error(ret.data));
		}
	} catch (error) {
		dispatch(AlertAction.ErrorMsg("Failed to load user list"));
	}
	dispatch({ type: AdminUserConstant.LOADED });
};

const UpdateUserInfo = (userid, userInfo) => async (dispatch) => {
	dispatch({ type: AdminUserConstant.LOADING });
	const who = readSessionAdminIdOrAbort(dispatch);
	if (!who) { dispatch({ type: AdminUserConstant.LOADED }); return false; }
	const data = {
		who,
		type: 'update',
		data: userInfo,
	};
	let ok = false;
	try {
		const ret = await axios.post(AppConfig.API_SERVER_PATH + '/user/' + userid, data, JWT());
		if (ret.data.status == 'success') {
			dispatch(AlertAction.Success(t('common.message.success.saved')));
			// FEAT-025 — 응답 user 의 dealerType legacy fallback 정규화 후 reducer 반영.
			dispatch({ type: AdminUserConstant.UPDATE_USERINFO, user: normalizeUserDealerType(ret.data.data) });
			// IMP-475 P2c — 본인 계정이면 저장본·화면 언어도 응답 값으로 맞춘다.
			if (userid === who) {
				await dispatch(AuthAction.ApplyMySettingsFromServer(ret.data.data));
			}
			ok = true;
		} else {
			// IMP-468 (#507): ErrorMsg 는 리듀서에서 errors 를 [] 로 덮어쓴다. 그 탓에 BE 가 보낸
			// 필드별 에러(EU0004 + errors[{name,msg}])가 화면에 닿지 못하고 "Update Failed" 만
			// 떴다. Error(ret.data) 로 보내 errors 배열을 리듀서까지 전달한다.
			dispatch(AlertAction.Error(ret.data));
		}
	} catch (error) {
		dispatch(AlertAction.Error(error));
	}
	dispatch({ type: AdminUserConstant.LOADED });
	return ok;
};

const RegistUserInfo = (userInfo) => async (dispatch) => {
	dispatch({ type: AdminUserConstant.LOADING });
	const who = readSessionAdminIdOrAbort(dispatch);
	if (!who) { dispatch({ type: AdminUserConstant.LOADED }); return; }
	const data = {
		who,
		type: 'regist',
		data: userInfo,
	};

	try {
		const ret = await axios.post(AppConfig.API_SERVER_PATH + '/user', data, JWT());
		if (ret.data.status == 'success') {
			dispatch({ type: AdminUserConstant.REGIST_USERINFO, user: normalizeUserDealerType(ret.data.data) });
			dispatch(AlertAction.Success(t('common.message.success.saved')));
		} else {
			// IMP-468 (#507): 위 UpdateUserInfo 와 같은 이유 — 필드별 에러를 리듀서까지 전달한다.
			dispatch(AlertAction.Error(ret.data));
		}
	} catch (error) {
		dispatch(AlertAction.Error(error));
	}
	dispatch({ type: AdminUserConstant.LOADED });
};

const DeleteUser = (userid) => async (dispatch) => {
	dispatch({ type: AdminUserConstant.LOADING });
	const who = readSessionAdminIdOrAbort(dispatch);
	if (!who) { dispatch({ type: AdminUserConstant.LOADED }); return; }
	const data = {
		who,
		type: 'delete',
	};
	try {
		const ret = await axios.post(AppConfig.API_SERVER_PATH + '/user/' + userid, data, JWT());
		if (ret.data.status == 'success') {
			dispatch(AlertAction.Success(t('common.message.deleted')));
			dispatch({ type: AdminUserConstant.DELETE_USER, deluserid: userid });
		} else {
			dispatch(AlertAction.Error(ret.data));
		}
	} catch (error) {
		dispatch(AlertAction.Error(error));
	}
	dispatch({ type: AdminUserConstant.LOADED });
};

const ClearUpdateResult = () => (dispatch) => {
	dispatch({ type: AdminUserConstant.CLEAR_UPDATERES });
};

const GetUser = (_id) => async (dispatch) => {
	dispatch({ type: AdminUserConstant.LOADING });
	try {
		const ret = await axios.get(AppConfig.API_SERVER_PATH + '/user/' + _id, JWT());
		if (ret.data.status == 'success') {
			// FEAT-025 — dealerType legacy fallback.
			dispatch({ type: AdminUserConstant.GETUSERINFO_SUCCESS, user: normalizeUserDealerType(ret.data.data) });
		} else {
			dispatch(AlertAction.ErrorMsg("Get Users Failed"));
		}
		dispatch({ type: AdminUserConstant.LOADED });
		return ret;
	} catch (error) {
		dispatch(AlertAction.ErrorMsg("Get Users Failed"));
		dispatch({ type: AdminUserConstant.LOADED });
		return null;
	}
};

const GetUserInfoByAdminId = (adminid) => async (dispatch) => {
	dispatch({ type: AdminUserConstant.LOADING });
	try {
		const ret = await axios.get(AppConfig.API_SERVER_PATH + '/user/' + adminid, JWT());

		if (ret.data.status == 'success') {
			dispatch({ type: AdminUserConstant.GETUSERINFO_SUCCESS, user: normalizeUserDealerType(ret.data.data) });
		}
	} catch (error) {
		dispatch(AlertAction.ErrorMsg("Failed to load user info"));
	}
	dispatch({ type: AdminUserConstant.LOADED });
}

const GetComboListByCompanyId = async (companyId) => {
	try {
		const user = readSessionUser();
		const targetCompanyId = companyId || user?.companyInfo?.companyId;
		if (!targetCompanyId) return [];
		const ret = await axios.get(AppConfig.API_SERVER_PATH + '/user/getcomboByCompanyId/' + targetCompanyId, JWT());
		if (ret.data.status == 'success') {
			return ret.data.data;
		} else {
			return [];
		}
	} catch (error) {
		return [];
	}
}

const GetUserListByCompanyId = async (companyId) => {
	try {
		const ret = await axios.get(AppConfig.API_SERVER_PATH + '/user/getUserListByCompanyId/' + companyId, JWT());
		if (ret.data.status == 'success') {
			return ret.data.data;
		} else {
			return [];
		}
	} catch (error) {
		return [];
	}
}

const GetReducer = (state) => {
	return state.AdminUserReducer;
};

export const AdminUserAction = {
	GetUserAll,
	GetUser,
	UpdateUserInfo,
	RegistUserInfo,
	ClearUpdateResult,
	DeleteUser,
	GetReducer,
	GetComboListByCompanyId,
	GetUserInfoByAdminId,
	GetUserListByCompanyId
};
